import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { borderPrimary } from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';
import { InventoryCount, InventoryCountItem } from '../../../services/inventory/inventoryService';
import { useInventoryViewStore as useInventoryStore } from '../../onboarding/practice/inventoryPractice';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  count: InventoryCount | null;
  restaurantId: string;
}

/**
 * The frozen €/counted-unit price of an item. Backed out of the item itself so it stays exactly
 * what was saved (never re-derived from live order prices). Not derivable when the item was
 * counted as 0 — the server resolves that case from the section's unit config on save.
 */
function pricePerCountUnit(item: InventoryCountItem): number | null {
  if (item.count <= 0 || item.countInStdUnit <= 0) return null;
  return item.pricePerStdUnit * (item.countInStdUnit / item.count);
}

function priceBreakdown(item: InventoryCountItem, unitPrice: number | null): string {
  if (unitPrice == null) return '—';
  return `${item.count} ${item.countUnit} * ${currencyFormatter.format(unitPrice)} / ${item.countUnit}`;
}

export default function InventoryCountDetailModal({ isOpen, onClose, count, restaurantId }: Props) {
  const { t } = useTranslation();
  const updateCountQuantities = useInventoryStore((s) => s.updateCountQuantities);

  // One edit mode for the whole list (the pencil in the header) — every product row turns
  // into an input at once and all corrections are saved in a single call.
  const [isEditing, setIsEditing] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Reset edit state whenever a different count is opened (or the modal is closed).
  useEffect(() => {
    if (isOpen) {
      setIsEditing(false);
      setDrafts({});
      setError(null);
      setSearchQuery('');
    }
  }, [count?.id, isOpen]);

  // Lock background page scroll while the drawer is open (same idiom as the
  // cost-price detail modals). Cleanup restores it on close or unmount.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const items = useMemo(() => count?.items ?? [], [count?.items]);

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => item.productName.toLowerCase().includes(query));
  }, [items, searchQuery]);

  // Every draft that is a valid, actually-changed number for a product in this count.
  const changedItems = useMemo(() => {
    return Object.entries(drafts)
      .map(([productKey, raw]) => ({ productKey, count: Number(raw), raw }))
      .filter(({ productKey, count, raw }) => {
        const item = items.find(i => i.productKey === productKey);
        if (!item) return false;
        return raw.trim() !== '' && Number.isFinite(count) && count >= 0 && count !== item.count;
      })
      .map(({ productKey, count }) => ({ productKey, count }));
  }, [drafts, items]);

  const hasInvalidDraft = useMemo(
    () => Object.values(drafts).some(raw => raw.trim() !== '' && (!Number.isFinite(Number(raw)) || Number(raw) < 0)),
    [drafts]
  );

  const handleSaveAll = async () => {
    if (!count?.id) return;
    if (changedItems.length === 0) {
      setIsEditing(false);
      setDrafts({});
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await updateCountQuantities(count.id, restaurantId, changedItems);
      setIsEditing(false);
      setDrafts({});
    } catch (err) {
      console.error('Failed to update inventory count quantities', err);
      setError(t('inventory.updateCountFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setDrafts({});
    setError(null);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-[#00000099] dark:bg-black/60 transition-opacity duration-300',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex flex-col w-full max-w-[584px]',
          'bg-white dark:bg-[#0A2133] shadow-2xl',
          'transform transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-[#E8E8E9] dark:border-[#1E3A4E] flex-shrink-0">
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-[#13354F] hover:bg-gray-50 dark:bg-[#13354F] transition-colors"
            aria-label={t('inventory.cancel') as string}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-black dark:text-[#CBD5E1]" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.8346 10H4.16797" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.0013 15.8327L4.16797 9.99935L10.0013 4.16602" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {/* Pencil = turn the whole product list into editable counts. */}
          <button
            onClick={() => (isEditing ? handleCancelEdit() : setIsEditing(true))}
            className={cn(
              'h-10 px-3 flex items-center gap-2 rounded-lg border transition-colors text-[14px] leading-5 font-semibold',
              isEditing
                ? 'border-[#0E8965] text-[#0E8965] bg-[#EDFCF5] dark:bg-[#0E89651A]'
                : 'border-[#E8E8E9] dark:border-[#13354F] text-[#344054] dark:text-[#CBD5E1] hover:bg-gray-50 dark:bg-[#13354F]'
            )}
            aria-label={t('inventory.editItem') as string}
          >
            <Pencil className="w-4 h-4" />
            {isEditing ? t('common.cancel') : t('common.edit')}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-3 sm:py-5 flex flex-col min-h-0">
          <div className="mb-5 sm:mb-6">
            <h2 className="mb-2 sm:mb-3 text-[24px] sm:text-[28px] leading-9 sm:leading-[39px] font-semibold sm:font-bold text-[#161924] dark:text-zinc-50">
              {count?.sectionNameSnapshot}
            </h2>

            {isEditing && (
              <p className={cn('text-[14px] leading-5 text-[#667085] dark:text-zinc-400 mb-3')}>{t('inventory.editCountHint')}</p>
            )}

            <div className="flex items-center gap-2 text-[14px] leading-[20px] font-medium text-[#667085] dark:text-[#CBD5E1]">
              <div className="flex items-center gap-1">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="7.9987" cy="8.00001" r="6.66667" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M8 5.33333V8L9.33333 9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {count?.countedAt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
              <div className="flex items-center gap-1">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 1.33333V2.66667M4 1.33333V2.66667" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M1.66602 8.16216C1.66602 5.25729 1.66602 3.80486 2.50076 2.90243C3.33551 2 4.67901 2 7.36602 2H8.63268C11.3197 2 12.6632 2 13.4979 2.90243C14.3327 3.80486 14.3327 5.25729 14.3327 8.16216V8.5045C14.3327 11.4094 14.3327 12.8618 13.4979 13.7642C12.6632 14.6667 11.3197 14.6667 8.63268 14.6667H7.36602C4.67901 14.6667 3.33551 14.6667 2.50076 13.7642C1.66602 12.8618 1.66602 11.4094 1.66602 8.5045V8.16216Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M2 5.33333H14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {count?.countedAt.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>

          {error && <p className={cn('text-[14px] leading-5 text-[#D92D20] dark:text-textRed-dark mb-4')}>{error}</p>}

          {/* Total inventory value card */}
          <div className="mb-4 sm:mb-6 p-3 sm:px-4 sm:py-5 rounded-xl border border-[#EAECF0] dark:border-[#0E2B42] bg-white dark:bg-[#0E2B42] shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
            <div className='flex items-center gap-3 mb-3 sm:mb-4'>
              <div className='w-[42px] h-[42px] rounded-lg bg-[#0794551A] dark:bg-[#0794551A] flex items-center justify-center'>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18.6673 17.3359C17.5562 17.3359 16.6118 16.947 15.834 16.1693C15.0562 15.3915 14.6673 14.447 14.6673 13.3359C14.6673 12.2248 15.0562 11.2804 15.834 10.5026C16.6118 9.72483 17.5562 9.33594 18.6673 9.33594C19.7784 9.33594 20.7229 9.72483 21.5007 10.5026C22.2784 11.2804 22.6673 12.2248 22.6673 13.3359C22.6673 14.447 22.2784 15.3915 21.5007 16.1693C20.7229 16.947 19.7784 17.3359 18.6673 17.3359ZM9.33398 21.3359C8.60065 21.3359 7.97287 21.0748 7.45065 20.5526C6.92843 20.0304 6.66732 19.4026 6.66732 18.6693V8.0026C6.66732 7.26927 6.92843 6.64149 7.45065 6.11927C7.97287 5.59705 8.60065 5.33594 9.33398 5.33594H28.0007C28.734 5.33594 29.3618 5.59705 29.884 6.11927C30.4062 6.64149 30.6673 7.26927 30.6673 8.0026V18.6693C30.6673 19.4026 30.4062 20.0304 29.884 20.5526C29.3618 21.0748 28.734 21.3359 28.0007 21.3359H9.33398ZM12.0007 18.6693H25.334C25.334 17.9359 25.5951 17.3082 26.1173 16.7859C26.6395 16.2637 27.2673 16.0026 28.0007 16.0026V10.6693C27.2673 10.6693 26.6395 10.4082 26.1173 9.88594C25.5951 9.36372 25.334 8.73594 25.334 8.0026H12.0007C12.0007 8.73594 11.7395 9.36372 11.2173 9.88594C10.6951 10.4082 10.0673 10.6693 9.33398 10.6693V16.0026C10.0673 16.0026 10.6951 16.2637 11.2173 16.7859C11.7395 17.3082 12.0007 17.9359 12.0007 18.6693ZM25.334 26.6693H4.00065C3.26732 26.6693 2.63954 26.4082 2.11732 25.8859C1.5951 25.3637 1.33398 24.7359 1.33398 24.0026V10.6693C1.33398 10.2915 1.46176 9.97483 1.71732 9.71927C1.97287 9.46372 2.28954 9.33594 2.66732 9.33594C3.0451 9.33594 3.36176 9.46372 3.61732 9.71927C3.87287 9.97483 4.00065 10.2915 4.00065 10.6693V24.0026H25.334C25.7118 24.0026 26.0284 24.1304 26.284 24.3859C26.5395 24.6415 26.6673 24.9582 26.6673 25.3359C26.6673 25.7137 26.5395 26.0304 26.284 26.2859C26.0284 26.5415 25.7118 26.6693 25.334 26.6693Z" fill="#079455" />
                </svg>
              </div>
              <p className={cn('text-[16px] sm:text-[18px] leading-6 text-[#101828] font-semibold dark:text-[#F8FAFC] ')}>
                {t('inventory.totalInventoryValue')}
              </p>
            </div>
            <div className='flex items-center justify-between gap-4'>
              <p className={cn('text-[24px] sm:text-[30px] leading-[38px] font-bold text-[#101828] dark:text-zinc-50')}>
                {currencyFormatter.format(count?.totalValue || 0)}
              </p>
              <p className={cn('text-[16px] sm:text-[18px] leading-5 font-medium text-[#475467] dark:text-[#CBD5E1]')}>
                {t('inventory.productCountLabel', { count: items.length })}
              </p>
            </div>
          </div>

          <h4 className='text-[16px] leading-5 text-[#0C111D] dark:text-[#F8FAFC] font-semibold mb-3 sm:mb-4'>{t('inventory.products')}</h4>

          <div className="mb-4 relative w-full">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085] dark:text-[#CBD5E1]" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <input
              type="text"
              placeholder={t('inventory.searchProducts') as string}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={cn(
                'w-full pl-10 pr-4 h-11 rounded-lg border dark:border-[#2D4F68] outline-none text-[16px] leading-6 font-normal',
                borderPrimary,
                'bg-white dark:bg-[#14344C]',
                'text-zinc-900 dark:text-zinc-50',
              )}
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar">
            <div className="rounded-lg p-3 sm:p-4 border border-[#E8E8E9] dark:border-[#2D4F68] bg-white dark:bg-[#0A2133] shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]">
              {filteredItems.map((item, idx) => {
                const unitPrice = pricePerCountUnit(item);
                const draft = drafts[item.productKey];
                const parsed = draft === undefined ? item.count : Number(draft);
                const rowInvalid = draft !== undefined && (draft.trim() === '' || !Number.isFinite(parsed) || parsed < 0);
                const rowValue = !rowInvalid && unitPrice != null ? parsed * unitPrice : item.value;

                if (isEditing) {
                  return (
                    <div
                      key={`${item.productKey}-${idx}`}
                      className="rounded-lg border border-[#E8E8E9] dark:border-[#2D4F68] p-3 mb-3 last:mb-0"
                    >
                      <p className="text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 mb-3">
                        {item.productName}
                      </p>

                      <div className="flex items-center justify-between gap-3 mb-3">
                        <span className="text-[14px] sm:text-[16px] leading-5 font-medium text-[#667085] dark:text-[#CBD5E1]">
                          {priceBreakdown(item, unitPrice)}
                        </span>
                        <span className="text-[16px] leading-5 font-semibold text-[#101828] dark:text-[#F8FAFC] shrink-0">
                          {currencyFormatter.format(item.value)}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={draft !== undefined ? draft : String(item.count)}
                          onChange={(e) => setDrafts((prev) => ({ ...prev, [item.productKey]: e.target.value }))}
                          className={cn(
                            'max-w-[80px] h-10 sm:h-8 px-2.5 py-[5px] rounded-lg border border-[#D0D5DD] dark:border-[#2D4F68]',
                            'bg-white dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-zinc-50 outline-none',
                            rowInvalid ? 'border-[#D92D20] text-[#D92D20]' : ''
                          )}
                        />

                        {/* The counted unit is fixed: it is the unit staff actually counted in and cannot be changed here. */}
                        <span className="h-10 sm:h-8 flex items-center px-2.5 py-[5px] rounded-lg border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-[#14344C] shrink-0 text-[14px] leading-5 text-[#101828] dark:text-[#F8FAFC]">
                          {item.countUnit}
                        </span>
                      </div>

                      {/* Only shown once the number actually moved — otherwise it just repeats the value above. */}
                      {draft !== undefined && !rowInvalid && parsed !== item.count && (
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-[14px] sm:text-[16px] font-medium leading-5 text-[#667085] dark:text-[#CBD5E1]">
                            {t('inventory.newValue')}
                          </span>
                          <span className="text-[16px] leading-5 font-semibold text-[#0E8965] dark:text-[#0E8965]">
                            {currencyFormatter.format(rowValue)}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={`${item.productKey}-${idx}`} className="pb-4 pt-4 first:pt-0 last:pb-0 border-b border-[#E8E8E9] dark:border-zinc-700 last:border-b-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className='mb-3 flex items-center justify-between gap-3'>
                          <p className={cn('text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50')}>
                            {item.productName}
                          </p>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="flex-1">
                            <p className={cn('text-[14px] sm:text-[16px] leading-5 sm:leading-6 text-[#667085] dark:text-[#CBD5E1]')}>
                              {item.count} {item.countUnit} * {unitPrice != null ? `${currencyFormatter.format(unitPrice)} / ${item.countUnit}` : '—'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className={cn('text-[16px] leading-5 sm:leading-6 font-semibold text-[#101828] dark:text-[#F8FAFC]')}>
                              {currencyFormatter.format(rowValue)}
                            </p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* Save bar — only while editing; one call corrects every changed product. */}
        {isEditing && (
          <div className="flex gap-3 sm:gap-6 sm:px-5 p-4 sm:py-5 flex-shrink-0 border-t border-[#E8E8E9] dark:border-[#1E3A4E]">
            <button
              onClick={handleCancelEdit}
              disabled={isSaving}
              className="bg-white flex-1 px-6 py-[11px] rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68] dark:bg-[#14344C] text-[14px] leading-5 font-semibold text-[#344054] dark:text-[#F8FAFC] disabled:opacity-50 transition-colors shadow-[0px_1px_2px_0px_#1018280D] dark:shadow-[0px_1px_2px_0px_#00000040]"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSaveAll}
              disabled={isSaving || hasInvalidDraft || changedItems.length === 0}
              className="flex-1 px-6 py-[11px] rounded-xl text-[14px] leading-5 font-semibold text-white dark:text-white bg-[#0E8965] dark:bg-[#0E8965] hover:bg-[#0E8965]/95 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
